import {BASE_API_URL} from '../config';
import history from './history';

const commonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json; charset=UTF-8',
};

const networkErrorMessage =
  'Unable to connect. Please check your connection and try again.';
const unexpectedResponseMessage =
  'The server returned an unexpected response. Please try again later.';

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
async function parseJSON(response) {
  if (!response) return null;
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  try {
    return await response.json();
  } catch (cause) {
    if (cause?.name === 'AbortError') throw cause;
    const error = new Error(unexpectedResponseMessage);
    error.status = response.status;
    error.code = 'INVALID_RESPONSE';
    error.cause = cause;
    throw error;
  }
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
async function checkStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  if (response.status === 401) {
    if (window.location.href.indexOf('partner/log') === -1) {
      history.push('/login');
    }
  }
  let payload = null;
  try {
    payload = await response.json();
  } catch (cause) {
    if (cause?.name === 'AbortError') throw cause;
    payload = null;
  }

  const nestedError =
    payload && typeof payload.error === 'object' ? payload.error : null;
  const fallbackMessage =
    (response.status === 403 &&
      'This request was blocked. Please refresh the page and try again.') ||
    (response.status === 429 &&
      'Too many requests. Please wait and try again.') ||
    response.statusText ||
    'Request failed. Please try again.';
  const message =
    [payload?.error, payload?.message, nestedError?.message].find(
      (value) => typeof value === 'string' && value.trim()
    ) || fallbackMessage;
  const error = new Error(message);
  error.status = response.status;
  error.code = payload?.code || nestedError?.code || '';
  error.payload = payload;
  const retryAfter =
    payload?.retryAfter ??
    nestedError?.retryAfter ??
    response.headers?.get('Retry-After');
  if (retryAfter !== undefined && retryAfter !== null) {
    error.retryAfter = retryAfter;
  }

  throw error;
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export default async function request(url, token, options = {}) {
  const completeUrl = BASE_API_URL + url;
  options.headers = options.headers || {};
  Object.assign(options.headers, commonHeaders);
  if (token) {
    Object.assign(options.headers, {
      Authorization: `Bearer ${token}`,
    });
  }
  let response;
  try {
    response = await fetch(completeUrl, options);
  } catch (cause) {
    if (cause?.name === 'AbortError') throw cause;
    // Browsers hide HTTP status for network and CORS failures.
    const error = new Error(networkErrorMessage);
    error.code = 'NETWORK_ERROR';
    error.cause = cause;
    throw error;
  }
  return parseJSON(await checkStatus(response));
}
